import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { WalletAPI } from '../services/api';
import { useBTCPrice } from '../hooks/useBTCPrice';

const WalletContext = createContext();

export const WalletProvider = ({ children }) => {
  const [walletData, setWalletData] = useState({
    balance: 0,
    locked_balance: 0,
    available_balance: 0,
    bitcoin_address: null,
    transactions: [],
    isLoading: false,
    error: null
  });

  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Use BTC Price Hook
  const { 
    price: btcPrice, 
    formattedPrice, 
    loading: priceLoading, 
    btcToUsd, 
    usdToBtc,
    refresh: refreshPrice 
  } = useBTCPrice(60000);

  // Fetch wallet balance
  const fetchBalance = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (walletData.isLoading) {
      // console.log('⚠️ fetchBalance: Already loading, skipping...');
      return;
    }
    
    // Add timestamp to prevent too frequent calls
    const now = Date.now();
    const lastCall = walletData.lastFetchTime || 0;
    const timeSinceLastCall = now - lastCall;
    
    if (timeSinceLastCall < 5000) { // 5 seconds minimum between calls
      // console.log(`⏱️ fetchBalance: Too soon (${timeSinceLastCall}ms), skipping...`);
      return;
    }
    
    try {
      // console.log('💰 fetchBalance: Starting...');
      setWalletData(prev => ({ ...prev, isLoading: true, error: null }));
      
      const response = await WalletAPI.getBalance();
      
      // console.log('💰 fetchBalance: Response received');
      
      if (response && response.success !== false) {
        const newData = {
          balance: parseFloat(response.balance || 0),
          locked_balance: parseFloat(response.locked_balance || 0),
          available_balance: parseFloat(response.available_balance || 0),
          bitcoin_address: response.bitcoin_address || null,
          isLoading: false,
          error: null,
          lastFetchTime: Date.now()
        };
        setWalletData(newData);
        setLastUpdated(new Date());
        return newData;
      } else {
        throw new Error(response?.error || 'Failed to fetch balance');
      }
    } catch (error) {
      // console.error('💥 fetchBalance error:', error);
      setWalletData(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Failed to fetch wallet data'
      }));
      throw error;
    }
  }, [walletData.isLoading, walletData.lastFetchTime]);

  // Fetch transactions
  const fetchTransactions = useCallback(async (limit = 20, page = 1) => {
    try {
      setTransactionsLoading(true);
      const response = await WalletAPI.getTransactions({ limit, page });
      
      if (response && response.success !== false) {
        setTransactions(response.transactions || []);
        return response.transactions || [];
      } else {
        throw new Error(response?.error || 'Failed to fetch transactions');
      }
    } catch (error) {
      // console.error('Error fetching transactions:', error);
      throw error;
    } finally {
      setTransactionsLoading(false);
    }
  }, []);

  // Refresh wallet data
  const refreshWallet = useCallback(async () => {
    await Promise.all([
      fetchBalance(),
      fetchTransactions()
    ]);
  }, [fetchBalance, fetchTransactions]);

  // Auto-refresh wallet every 30 seconds if balance > 0
  useEffect(() => {
    let intervalId;
    
    if (walletData.balance > 0 && !walletData.isLoading) {
      intervalId = setInterval(() => {
        fetchBalance();
      }, 30000); // 30 seconds
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [walletData.balance, walletData.isLoading, fetchBalance]);

  // --- USD Formatting Functions ---

  /**
   * Format BTC amount to USD with 2 decimal places
   */
  const formatUSD = useCallback((btcAmount) => {
    if (!btcPrice) return '0.00';
    const usdAmount = btcToUsd(btcAmount);
    return usdAmount.toFixed(2);
  }, [btcPrice, btcToUsd]);

  /**
   * Format BTC amount with 8 decimal places
   */
  const formatBTC = useCallback((amount) => {
    return parseFloat(amount || 0).toFixed(8);
  }, []);

  /**
   * Format BTC amount as USD with $ symbol
   */
  const formatUSDWithSymbol = useCallback((btcAmount) => {
    if (!btcPrice) return '$0.00';
    const usdAmount = btcToUsd(btcAmount);
    return `$${usdAmount.toFixed(2)}`;
  }, [btcPrice, btcToUsd]);

  /**
   * Get formatted balances in USD (primary) and BTC (secondary)
   */
  const getFormattedBalances = useCallback(() => {
    const btcBalance = walletData.balance;
    const btcLocked = walletData.locked_balance;
    const btcAvailable = walletData.available_balance;

    return {
      // USD values (primary display)
      balance: formatUSD(btcBalance),
      locked: formatUSD(btcLocked),
      available: formatUSD(btcAvailable),
      
      // USD with $ symbol
      balanceFormatted: formatUSDWithSymbol(btcBalance),
      lockedFormatted: formatUSDWithSymbol(btcLocked),
      availableFormatted: formatUSDWithSymbol(btcAvailable),
      
      // BTC values (secondary/reference)
      btc: {
        balance: formatBTC(btcBalance),
        locked: formatBTC(btcLocked),
        available: formatBTC(btcAvailable)
      },
      
      // Combined display (USD + BTC)
      display: {
        balance: `${formatUSDWithSymbol(btcBalance)} (≈${formatBTC(btcBalance)} BTC)`,
        locked: `${formatUSDWithSymbol(btcLocked)} (≈${formatBTC(btcLocked)} BTC)`,
        available: `${formatUSDWithSymbol(btcAvailable)} (≈${formatBTC(btcAvailable)} BTC)`
      },
      
      // Raw numeric values
      raw: {
        balance: btcBalance,
        locked: btcLocked,
        available: btcAvailable
      }
    };
  }, [walletData, formatUSD, formatUSDWithSymbol, formatBTC]);

  /**
   * Get transaction with USD conversion
   */
  const getTransactionWithUSD = useCallback((transaction) => {
    const btcAmount = parseFloat(transaction.amount || 0);
    const usdAmount = btcToUsd(btcAmount);
    
    return {
      ...transaction,
      amount_btc: btcAmount,
      amount_usd: usdAmount,
      amount_usd_formatted: `$${usdAmount.toFixed(2)}`,
      fee_usd: transaction.fee ? btcToUsd(parseFloat(transaction.fee)) : 0,
      fee_usd_formatted: transaction.fee ? `$${btcToUsd(parseFloat(transaction.fee)).toFixed(2)}` : '$0.00'
    };
  }, [btcToUsd]);

  /**
   * Get transactions with USD conversion
   */
  const getTransactionsWithUSD = useCallback(() => {
    return transactions.map(tx => getTransactionWithUSD(tx));
  }, [transactions, getTransactionWithUSD]);

  /**
   * Get wallet summary with USD values
   */
  const getWalletSummary = useCallback(() => {
    const formatted = getFormattedBalances();
    return {
      total_btc: walletData.balance,
      total_usd: formatUSDWithSymbol(walletData.balance),
      available_btc: walletData.available_balance,
      available_usd: formatUSDWithSymbol(walletData.available_balance),
      locked_btc: walletData.locked_balance,
      locked_usd: formatUSDWithSymbol(walletData.locked_balance),
      btc_price: btcPrice,
      btc_price_formatted: formattedPrice,
      last_updated: lastUpdated
    };
  }, [walletData, btcPrice, formattedPrice, lastUpdated, formatUSDWithSymbol]);

  const value = {
    // Original wallet data
    walletData,
    transactions,
    transactionsLoading,
    lastUpdated,
    
    // BTC Price info
    btcPrice,
    formattedPrice,
    priceLoading,
    
    // Fetch functions
    fetchBalance,
    fetchTransactions,
    refreshWallet,
    refreshPrice,
    
    // Formatting functions
    formatUSD,
    formatBTC,
    formatUSDWithSymbol,
    getFormattedBalances,
    getTransactionWithUSD,
    getTransactionsWithUSD,
    getWalletSummary,
    
    // Legacy/Compatibility
    formatCurrency: formatBTC,
    isLoading: walletData.isLoading,
    error: walletData.error
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export default WalletContext;